import { FC } from 'react';
import {
  Control,
  Path,
  UseFieldArrayRemove,
  UseFormSetValue,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import styled from 'styled-components';

import { FormLayout, Label, Larger } from '@/components/Forms';
import VideoField from '@/components/Forms/Fields/VideoField';
import FormField from '@/components/Forms/FormField';
import ImageField from '@/components/Forms/ImageField';
import { BatchBox, BatchBoxHeading, RemoveButton } from '@/components/Testing';

import { FormDataT } from './FinalisationFormModal';

const StyledBatchBox = styled(BatchBox)`
  &:first-child {
    margin-top: 0;
  }
`;

interface Props {
  control: Control<FormDataT>;
  index: number;
  removeSet: UseFieldArrayRemove;
  setValue: UseFormSetValue<FormDataT>;
  isForTitles: boolean;
}

const FinalisationSetFields: FC<Props> = ({
  control,
  index,
  removeSet,
  setValue,
  isForTitles,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `sets.${index}.variations`,
  });

  const pasteFileName = useWatch({
    control,
    name: 'paste_file_name',
  });

  const onThumbnailsUploaded = (
    images: Array<{ file: File; path?: string }>,
  ) => {
    images
      .filter(({ path }) => !!path)
      .forEach(({ file, path }) =>
        append({
          image: path!,
          imageFile: file,
          name: isForTitles
            ? pasteFileName
              ? '{video} - {thumb} - {title}'
              : '{title} - {video} - THUMB {thumb} default'
            : pasteFileName
              ? '{video} - {thumb}'
              : '{video} - THUMB {thumb}',
        }),
      );
  };

  const prefixFieldName = (
    name: 'name' | 'image' | 'imageFile',
    variationIndex: number,
  ) => `sets.${index}.variations.${variationIndex}.${name}` as Path<FormDataT>;

  const onVideoUploaded = (file: File) => {
    setValue(
      `sets.${index}.videoFilename`,
      file.name.split('.').slice(0, -1).join('.'),
    );
  };

  return (
    <StyledBatchBox>
      <BatchBoxHeading $type="button">
        Set #{index + 1}
        <RemoveButton type="button" onClick={() => removeSet(index)}>
          Remove
        </RemoveButton>
      </BatchBoxHeading>
      <FormLayout>
        {!isForTitles && (
          <FormField<FormDataT>
            control={control}
            label="Title"
            type="text"
            name={`sets.${index}.title`}
            placeholder="Add title"
          />
        )}
        {fields.map((field, variationIndex) => (
          <Larger key={field.id}>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                label="Variation name"
                type="text"
                name={prefixFieldName('name', variationIndex)}
                placeholder="Add name"
              />
              <Larger>
                <ImageField
                  notes="Supports: JPG, PNG, GIF. Size: 1280 x 720 / Up to 2MB"
                  id={`thumbnail-${index}-${variationIndex}-field`}
                  pathName={prefixFieldName('image', variationIndex) as string}
                  fileName={
                    prefixFieldName('imageFile', variationIndex) as string
                  }
                  onUploaded={onThumbnailsUploaded}
                  onDelete={() => remove(variationIndex)}
                />
              </Larger>
            </FormLayout>
          </Larger>
        ))}
        <Larger>
          {fields.length === 0 && <Label>Thumbs</Label>}
          <ImageField
            multiple
            notes="Supports: JPG, PNG, GIF. Size: 1280 x 720 / Up to 2MB<br />Upload multiple files to create multiple variations"
            id={`thumbnail-${index}-field`}
            onUploaded={onThumbnailsUploaded}
          />
        </Larger>
        <Larger>
          <Label>Video</Label>
          <VideoField<FormDataT>
            name={`sets.${index}.video`}
            videoUploadedCallback={onVideoUploaded}
            control={control}
          />
        </Larger>
      </FormLayout>
    </StyledBatchBox>
  );
};

export default FinalisationSetFields;
